import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, MessageSquare, CheckCircle2, Gauge, UserCheck, BadgeEuro, ShieldCheck, Star, Sparkles } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { CategoryIllustration } from "../components/CategoryIllustration";
import { WhatsappButton } from "../components/WhatsappButton";
import { useSite } from "../context/SiteContext";

const STEPS = [
  { icon: Sparkles, title: "Scegli", text: "Esplora le offerte di Telco, Fotovoltaico, Luce, Gas e TV e trova quella giusta per te." },
  { icon: MessageSquare, title: "Richiedi", text: "Clicca su WhatsApp: il messaggio è già precompilato con il prodotto scelto." },
  { icon: CheckCircle2, title: "Attiva", text: "Un consulente ti ricontatta, finalizza l'attivazione e applica eventuali bonus." },
];

const ADVANTAGES = [
  { icon: Gauge, title: "Veloce", text: "In pochi minuti confrontiamo e troviamo la soluzione più vantaggiosa per te." },
  { icon: UserCheck, title: "Personale", text: "Trasparenza totale e offerte su misura per le tue esigenze." },
  { icon: BadgeEuro, title: "Gratuito", text: "Nessuna commissione a carico tua: il nostro servizio è sempre gratis." },
  { icon: ShieldCheck, title: "Sicuro", text: "Solo canali ufficiali e consulenti certificati per la tua tranquillità." },
];

const TESTIMONIALS = [
  { name: "Marco R.", role: "Cliente residenziale", text: "Ho attivato fibra e fotovoltaico con un'unica chat WhatsApp. Veloce e chiaro, zero stress." },
  { name: "Giulia T.", role: "Titolare attività", text: "Bolletta luce e gas dimezzata per il mio negozio. Consulenza precisa e professionale." },
  { name: "Andrea P.", role: "Cliente streaming", text: "Mi hanno trovato il pacchetto TV perfetto. Risposte immediate e nessuna sorpresa." },
];

export default function HomePage() {
  const { categories } = useSite();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      {/* HERO + category grid (facile.it style) */}
      <section className="relative bg-surface border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-12">
          <div className="text-center max-w-3xl mx-auto animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-brand-light text-brand-darker text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full">
              <Zap className="w-3.5 h-3.5" /> Telco · Energia · Solare · TV
            </div>
            <h1 className="mt-5 font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05]">
              Noi confrontiamo,<br /><span className="text-brand-dark">tu risparmi.</span>
            </h1>
            <p className="mt-5 text-lg text-navy-soft max-w-xl mx-auto">
              Fibra, mobile, luce, gas, fotovoltaico e TV: scegli l'offerta e invia la richiesta in un clic su WhatsApp.
            </p>
          </div>

          {/* Category cards */}
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {categories.map((c, i) => (
              <Link
                key={c.slug}
                to={`/servizi/${c.slug}`}
                data-testid={`service-card-${c.slug}`}
                className="group bg-white rounded-2xl border border-line p-6 flex flex-col soft-shadow hover:soft-shadow-lg hover:-translate-y-1.5 transition-all duration-300 animate-fade-up"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <CategoryIllustration icon={c.icon} className="w-20 h-20 group-hover:scale-105 transition-transform" />
                <h3 className="mt-5 font-heading font-bold text-xl text-navy">{c.name}</h3>
                <p className="mt-2 text-sm text-navy-soft leading-relaxed flex-1">{c.description}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-brand-dark font-bold text-sm group-hover:gap-2.5 transition-all">
                  Scopri le offerte <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section className="max-w-7xl mx-auto w-full px-5 sm:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="label-eyebrow text-brand-dark mb-3">I nostri vantaggi</div>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-navy">
            Il modo più semplice di gestire le utenze.
          </h2>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {ADVANTAGES.map((a, i) => (
            <div key={i} className="text-center px-4" data-testid={`advantage-${i}`}>
              <div className="w-16 h-16 mx-auto rounded-full bg-brand-light text-brand-darker flex items-center justify-center">
                <a.icon className="w-7 h-7" />
              </div>
              <h3 className="mt-4 font-heading font-bold text-lg text-navy">{a.title}</h3>
              <p className="mt-2 text-sm text-navy-soft leading-relaxed">{a.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-surface border-y border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="label-eyebrow text-brand-dark mb-3">Come funziona</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-navy">Attiva in 3 semplici step.</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <div key={i} className="relative bg-white rounded-2xl border border-line p-7 soft-shadow" data-testid={`how-step-${i}`}>
                <div className="absolute -top-4 left-7 w-9 h-9 rounded-full bg-brand text-white font-heading font-extrabold flex items-center justify-center soft-shadow">
                  {i + 1}
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <s.icon className="w-6 h-6 text-brand-dark" />
                  <h3 className="font-heading font-bold text-lg text-navy">{s.title}</h3>
                </div>
                <p className="mt-3 text-sm text-navy-soft leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto w-full px-5 sm:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="flex items-center justify-center gap-1 mb-3">
            {[...Array(5)].map((_, j) => <Star key={j} className="w-5 h-5 fill-brand text-brand" />)}
          </div>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-navy">Fidati di chi ha già risparmiato.</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="bg-white rounded-2xl border border-line p-7 soft-shadow" data-testid={`testimonial-${i}`}>
              <div className="flex gap-0.5 mb-4">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-brand text-brand" />)}
              </div>
              <p className="text-navy leading-relaxed">"{t.text}"</p>
              <div className="mt-5 pt-5 border-t border-line">
                <div className="font-bold text-sm text-navy">{t.name}</div>
                <div className="text-xs text-navy-soft">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-navy text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 flex flex-col lg:flex-row items-center justify-between gap-8 text-center lg:text-left">
          <div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl leading-tight max-w-xl">
              Pronto a risparmiare sulle tue utenze?
            </h2>
            <p className="mt-4 text-white/75 text-lg max-w-lg">
              Scrivici su WhatsApp. Nessun impegno, risposta in pochi minuti.
            </p>
          </div>
          <WhatsappButton
            product="una consulenza gratuita"
            category="Consulenza"
            label="Scrivici su WhatsApp"
            testId="final-cta-whatsapp"
            className="text-base px-7 py-4 shrink-0"
          />
        </div>
      </section>

      <Footer />
    </div>
  );
}
